import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, ChevronRight, Lock, Check, Dumbbell, BookOpen, Footprints, ClipboardCheck, X } from "lucide-react";
import CurriculumVideoCard from "@/components/curriculum/CurriculumVideoCard";

const PHASE_STYLE = {
  RESET:     { bg: "bg-primary",     text: "text-primary-foreground" },
  ESTABLISH: { bg: "bg-blue-600",    text: "text-white" },
  PROGRESS:  { bg: "bg-emerald-600", text: "text-white" },
  "OWN IT":  { bg: "bg-foreground",  text: "text-background" },
};

function CheckinModal({ week, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    hardest: initial?.hardest || "",
    workouts_completed: initial?.workouts_completed || 0,
    steps_average_ok: !!initial?.steps_average_ok,
    keep_next_week: initial?.keep_next_week || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/journey/week/${week}/checkin`, form);
      await api.post(`/journey/week/${week}/complete`);
      toast.success(`Week ${week} checked in and marked complete.`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-foreground/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-lg bg-background rounded-lg border border-border my-8" onClick={(e) => e.stopPropagation()} data-testid="week-checkin-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="font-head uppercase">Week {week} · Check-in</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">What felt hardest this week?</label>
            <textarea value={form.hardest} onChange={(e) => setForm({ ...form, hardest: e.target.value })}
              rows={2} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="checkin-hardest" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">How many workouts did you complete? (0–3)</label>
            <input type="number" min={0} max={3} value={form.workouts_completed}
              onChange={(e) => setForm({ ...form, workouts_completed: Number(e.target.value || 0) })}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="checkin-workouts" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.steps_average_ok} onChange={(e) => setForm({ ...form, steps_average_ok: e.target.checked })} data-testid="checkin-steps" />
              I hit my step target most days this week
            </label>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">One thing to keep next week?</label>
            <textarea value={form.keep_next_week} onChange={(e) => setForm({ ...form, keep_next_week: e.target.value })}
              rows={2} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="checkin-keep" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary btn-sm disabled:opacity-40" data-testid="checkin-submit">
            {saving ? "Saving…" : "Save & Mark Week Complete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WeekDetail() {
  const { n } = useParams();
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [tab, setTab] = useState("A");
  const [showCheckin, setShowCheckin] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/journey/week/${n}`);
      setW(r.data);
    } catch (e) {
      if (e.response?.status === 423) toast.error("This week is not unlocked yet.");
      else toast.error(formatApiError(e.response?.data?.detail));
      nav("/app", { replace: true });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [n]);

  const workout = useMemo(() => w?.workouts.find((x) => x.template === tab) || w?.workouts?.[0], [w, tab]);

  if (!w) return <div className="text-muted-foreground text-sm">Loading week…</div>;

  const phaseS = PHASE_STYLE[w.phase] || PHASE_STYLE.RESET;
  const prev = Number(n) > 1 ? `/app/week/${Number(n) - 1}` : null;
  const next = Number(n) < 12 ? `/app/week/${Number(n) + 1}` : null;

  return (
    <div className="space-y-8" data-testid={`week-detail-${n}`}>
      <div className="flex items-center justify-between gap-3">
        <Link to="/app" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 hover:text-primary">
          <ChevronLeft size={14} /> Dashboard
        </Link>
        <div className="flex gap-2">
          {prev && <Link to={prev} className="btn-secondary btn-sm">← Wk {Number(n) - 1}</Link>}
          {next && <Link to={next} className="btn-secondary btn-sm">Wk {Number(n) + 1} →</Link>}
        </div>
      </div>

      {/* Week hero — Master Curriculum module + key message */}
      <div className={`${phaseS.bg} ${phaseS.text} rounded-lg p-8 md:p-10`}>
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Stage · {w.phase_label || w.phase} · {w.phase_message}</div>
        <div className="flex items-end justify-between gap-6 flex-wrap mt-3">
          <div className="min-w-0 flex-1">
            <div className="opacity-80 text-sm">Week {w.week} · Module {w.week}</div>
            <div className="wordmark text-3xl md:text-5xl leading-tight uppercase mt-2">{w.module_name || w.focus}</div>
            {w.key_message && <p className="mt-4 text-sm md:text-base opacity-90 max-w-2xl leading-relaxed">{w.key_message}</p>}
          </div>
          <div className="text-right shrink-0">
            <div className="opacity-80 text-xs uppercase tracking-widest">Week</div>
            <div className="wordmark text-5xl md:text-7xl leading-none">{w.week}</div>
            <div className="text-[10px] uppercase tracking-widest opacity-70 mt-1">of 12</div>
          </div>
        </div>
        {w.status === "complete" && (
          <div className="mt-6 inline-flex items-center gap-2 bg-background/20 rounded-md px-3 py-1.5 text-xs uppercase tracking-widest">
            <Check size={14} strokeWidth={3} /> Completed
          </div>
        )}
      </div>

      {/* WEEKLY LESSON — Curriculum Video Placeholder for this week (no timeline here — Dashboard shows the full strip) */}
      <CurriculumVideoCard weekNumber={Number(n)} showTimeline={false} />

      {/* Phase transition banner (weeks 3/6/9/12) */}
      {w.phase_transition && (
        <div className="rounded-lg p-5 bg-card border border-primary/40" data-testid="phase-transition">
          <div className="label-eyebrow text-primary">Phase transition</div>
          <div className="mt-2 font-head uppercase text-lg md:text-xl">{w.phase_transition.title}</div>
          <div className="mt-1 text-sm text-muted-foreground max-w-2xl">{w.phase_transition.body}</div>
        </div>
      )}

      {/* Workouts A/B/C */}
      <div id="workouts">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-eyebrow">This Week's Sessions</div>
            <h2 className="wordmark mt-2 text-3xl uppercase">3 Workouts. Same Templates.</h2>
          </div>
          <div className="flex gap-1.5" data-testid="workout-tabs">
            {w.workouts.map((wo) => (
              <button key={wo.template} onClick={() => setTab(wo.template)}
                className={`px-4 py-2 rounded-md text-xs font-head uppercase tracking-widest border transition-colors ${tab === wo.template ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:border-primary"}`}
                data-testid={`workout-tab-${wo.template}`}>
                Workout {wo.template}
              </button>
            ))}
          </div>
        </div>

        {workout && (
          <div className="mt-5 bg-card border border-border rounded-lg p-6" data-testid={`workout-${workout.template}`}>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="label-eyebrow">Workout {workout.template}</div>
                <h3 className="wordmark mt-2 text-2xl uppercase">{workout.title}</h3>
                <p className="mt-2 text-sm text-primary">{workout.cue}</p>
              </div>
              <Dumbbell className="text-primary" size={28} strokeWidth={2} />
            </div>
            {workout.illustration && (
              <div className="mt-5 rounded-md overflow-hidden border border-border bg-foreground">
                <img src={workout.illustration} alt={`Week ${w.week} — Workout ${workout.template} — ${workout.title}`} loading="lazy" className="w-full h-auto block" data-testid={`week-illustration-${workout.template}`} />
              </div>
            )}
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3">Exercise</th>
                    <th className="py-2 pr-3">Sets</th>
                    <th className="py-2 pr-3">Reps</th>
                    <th className="py-2 pr-3">RIR</th>
                    <th className="py-2 pr-3">Alt</th>
                    <th className="py-2">Coaching Focus</th>
                  </tr>
                </thead>
                <tbody>
                  {workout.exercises.map((ex, i) => (
                    <tr key={i} className="border-b border-border/60 align-top">
                      <td className="py-2.5 pr-3 font-head uppercase text-sm">{ex.name}</td>
                      <td className="py-2.5 pr-3">{ex.sets}</td>
                      <td className="py-2.5 pr-3">{ex.reps}</td>
                      <td className="py-2.5 pr-3">{ex.rir}</td>
                      <td className="py-2.5 pr-3 text-muted-foreground text-xs">{ex.alt}</td>
                      <td className="py-2.5 text-muted-foreground text-xs max-w-xs">{ex.cue || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Weekly Action — Master Curriculum */}
      {w.weekly_action && (
        <div className="bg-primary/5 border border-primary/40 rounded-lg p-6 flex items-start gap-4" data-testid="weekly-action">
          <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground inline-flex items-center justify-center shrink-0">
            <ClipboardCheck size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="label-eyebrow text-primary">Weekly Action</div>
            <div className="mt-2 text-sm">{w.weekly_action}</div>
          </div>
        </div>
      )}

      {/* Lesson — longevity curriculum (full lesson page has transcript, action, targets) */}
      <Link to={`/app/lesson/${w.week}`} className="block bg-foreground text-background rounded-lg p-7 hover:bg-primary transition-colors group" data-testid="week-lesson-link">
        <div className="flex items-center gap-2 label-eyebrow !text-background/70"><BookOpen size={14} strokeWidth={2.5} /> Weekly Lesson · {w.phase_label}</div>
        <h3 className="wordmark mt-3 text-3xl uppercase">{w.lesson_title}</h3>
        <div className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary group-hover:text-primary-foreground">Open lesson <ChevronRight size={14} /></div>
      </Link>

      {/* Movement */}
      <div className="bg-card border border-border rounded-lg p-6 flex items-start gap-4">
        <div className="w-10 h-10 rounded-md bg-primary/10 text-primary inline-flex items-center justify-center shrink-0">
          <Footprints size={20} strokeWidth={2.25} />
        </div>
        <div>
          <div className="label-eyebrow">Daily Movement Target</div>
          <div className="mt-2 text-sm">{w.movement_target}</div>
          <Link to="/app/move" className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary hover:underline">Log a walk <ChevronRight size={12} /></Link>
        </div>
      </div>

      {/* Check-in */}
      <div className="bg-card border border-border rounded-lg p-7">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 label-eyebrow"><ClipboardCheck size={14} strokeWidth={2.5} /> Weekly Check-in</div>
            <div className="mt-2 font-head uppercase text-lg">
              {w.checkin ? "You've checked in for this week." : "Reflect. Log. Unlock next week."}
            </div>
            {w.checkin && <p className="mt-1 text-xs text-muted-foreground">Hardest: "{w.checkin.hardest || '—'}" · Workouts: {w.checkin.workouts_completed || 0}/3</p>}
          </div>
          <button onClick={() => setShowCheckin(true)} className="btn-primary btn-sm" data-testid="open-checkin">
            {w.checkin ? "Update check-in" : "Start check-in"}
          </button>
        </div>
      </div>

      {showCheckin && (
        <CheckinModal week={Number(n)} initial={w.checkin}
          onClose={() => setShowCheckin(false)}
          onSaved={async () => { setShowCheckin(false); await load(); }} />
      )}
    </div>
  );
}
